import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect } from "react";
import routes, { RouteAccessType, hiddenRoutes } from "routes";
import PermissionsApi from "api/permissions";

const usePermissions = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await PermissionsApi.fetchGroups();
        setGroups(response.data);
        setLoadingGroups(false);
      } catch (error) {
        setError(obtenerMensajeError(error, "Error al cargar los grupos"));
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!selectedGroup) return;
      try {
        const response = await PermissionsApi.fetchPermissions(selectedGroup.id);
        const isUserAdmin = selectedGroup.roles?.includes("ADMIN");
        const processRoutes = (routeList) => {
          let processedRoutes = [];
          let currentId = 1;
          // Procesar rutas regulares
          routeList.forEach((route) => {
            if (route.type === "title") {
              // Solo procesar el título si no es ADMIN o si el usuario es admin
              if (route.access !== "ADMIN" || isUserAdmin) {
                processedRoutes.push({
                  id: currentId++,
                  route: `title-${route.key}`,
                  name: route.title,
                  isTitle: true,
                  access: route.access,
                  has_access:
                    response.data.find((p) => p.route === `title-${route.key}`)?.has_access ??
                    false,
                });
              }
            } else if (route.type === "nested") {
              route.children.forEach((child) => {
                if (
                  child.access === RouteAccessType.PROTECTED ||
                  (child.access === RouteAccessType.ADMIN && isUserAdmin)
                ) {
                  const hasAccess =
                    response.data.find((p) => p.route === child.route)?.has_access ?? false;

                  processedRoutes.push({
                    id: currentId++,
                    route: child.route,
                    name: child.name,
                    icon: child.icon,
                    access: child.access,
                    has_access: hasAccess,
                    component: child.component,
                  });
                }
              });
            } else if (
              route.access === RouteAccessType.PROTECTED ||
              (route.access === RouteAccessType.ADMIN && isUserAdmin)
            ) {
              processedRoutes.push({
                id: currentId++,
                route: route.route,
                name: route.name,
                icon: route.icon,
                access: route.access,
                has_access: response.data.find((p) => p.route === route.route)?.has_access ?? false,
                component: route.component,
              });
            }
          });

          // Procesar rutas ocultas (hiddenRoutes)
          hiddenRoutes.forEach((hiddenRoute) => {
            if (
              hiddenRoute.access === RouteAccessType.PROTECTED ||
              (hiddenRoute.access === RouteAccessType.ADMIN && isUserAdmin)
            ) {
              const hasAccess =
                response.data.find((p) => p.route === hiddenRoute.route)?.has_access ?? false;

              processedRoutes.push({
                id: currentId++,
                route: hiddenRoute.route,
                name: hiddenRoute.name,
                icon: null, // Las rutas ocultas no tienen icono
                access: hiddenRoute.access,
                has_access: hasAccess,
                component: hiddenRoute.component,
                isHidden: true, // Marcar como ruta oculta para identificarla
              });
            }
          });

          return processedRoutes;
        };

        const allRoutes = processRoutes(routes);
        setPermissions(allRoutes);
      } catch (error) {
        setError(obtenerMensajeError(error, "Error al cargar los permisos del grupo"));
      }
    };

    fetchPermissions();
  }, [selectedGroup]);

  const updatePermission = (id, hasAccess) => {
    setPermissions(
      permissions.map((perm) => {
        if (perm.id === id) {
          return { ...perm, has_access: hasAccess };
        }
        return perm;
      })
    );
  };

  const savePermissions = async () => {
    if (!selectedGroup) return;

    setSavingPermissions(true);
    try {
      const permissionsToSave = permissions
        .filter((p) => !p.isTitle || p.route.startsWith("title-"))
        .map((p) => ({
          route: p.route,
          has_access: p.has_access,
        }));

      await PermissionsApi.savePermissions(selectedGroup.id, permissionsToSave);
    } catch (error) {
      setError(obtenerMensajeError(error, "Error al guardar los permisos"));
    } finally {
      setSavingPermissions(false);
    }
  };

  return {
    groups,
    selectedGroup,
    setSelectedGroup,
    permissions,
    updatePermission,
    savePermissions,
    loadingGroups,
    savingPermissions,
    error,
  };
};

export default usePermissions;
